import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { noop } from 'lodash';
import {
    FacebookLoginButton,
    GoogleLoginButton,
    AppleLoginButton,
} from '@zocdoc-frontend-common/components-sociallogin';
import * as styled from './primitives.js';

import { PATIENT_MODE, PROVIDER_MODE, LOGIN_TYPE } from '../../constants';

const redirectUser = suggestedRedirect => {
    window.location.assign(suggestedRedirect);
};

export class SignInFormPatientProviderModeView extends Component {
    state = {
        // initialMode prop is seed data for isPatientMode state
        isPatientMode: this.props.initialMode === PATIENT_MODE,
    };

    static propTypes = {
        allowToggle: PropTypes.bool,
        facebookLoginUrl: PropTypes.string,
        googleLoginUrl: PropTypes.string,
        appleLoginUrl: PropTypes.string,
        initialMode: PropTypes.oneOf([PATIENT_MODE, PROVIDER_MODE]),
        onClickCreateAccount: PropTypes.func,
        onClickFacebookLogin: PropTypes.func,
        onClickGoogleLogin: PropTypes.func,
        onClickAppleLogin: PropTypes.func,
        onClickToggle: PropTypes.func,
        rememberMe: PropTypes.bool,
        returnUrl: PropTypes.string,
        showFacebookLogin: PropTypes.bool,
        showGoogleLogin: PropTypes.bool,
        showAppleLogin: PropTypes.bool,
        showRememberMe: PropTypes.bool,
        socialLoginValidationError: PropTypes.number,
        trackClickCreateAccount: PropTypes.func,
    };

    static defaultProps = {
        allowToggle: false,
        facebookLoginUrl: '',
        googleLoginUrl: '',
        appleLoginUrl: '',
        initialMode: PATIENT_MODE,
        onClickCreateAccount: noop,
        onClickFacebookLogin: noop,
        onClickGoogleLogin: noop,
        onClickAppleLogin: noop,
        onClickToggle: noop,
        rememberMe: true,
        returnUrl: '',
        showGoogleLogin: false,
        showFacebookLogin: false,
        showAppleLogin: false,
        showRememberMe: false,
        trackClickCreateAccount: noop,
    };

    toggleLoginMode = event => {
        const { allowToggle, onClickToggle } = this.props;

        if (allowToggle) {
            onClickToggle(event);

            this.setState({
                isPatientMode: !this.state.isPatientMode,
            });
        }
    };

    maybeRenderPatientModeButtons() {
        return this.state.isPatientMode ? (
            <div data-test="patient-mode-login-buttons">
                {this.maybeRenderGoogleLoginButton()}
                {this.maybeRenderFacebookLoginButton()}
                {this.maybeRenderAppleLoginButton()}
                {this.renderSignUpButton()}
            </div>
        ) : null;
    }

    maybeRenderGoogleLoginButton() {
        const { showGoogleLogin } = this.props;

        return showGoogleLogin ? (
            <styled.SocialLoginButtonWrapper>
                <GoogleLoginButton
                    data-test="google-login-button"
                    onClick={this.handleClickGoogleLogin}
                />
            </styled.SocialLoginButtonWrapper>
        ) : null;
    }

    // we won't enable this until ios13 ships
    maybeRenderAppleLoginButton() {
        const { showAppleLogin } = this.props;

        return showAppleLogin ? (
            <styled.SocialLoginButtonWrapper>
                <AppleLoginButton
                    data-test="apple-login-button"
                    onClick={this.handleClickAppleLogin}
                />
            </styled.SocialLoginButtonWrapper>
        ) : null;
    }

    maybeRenderFacebookLoginButton() {
        const { showFacebookLogin } = this.props;

        return showFacebookLogin ? (
            <styled.SocialLoginButtonWrapper>
                <FacebookLoginButton
                    data-test="facebook-login-button"
                    onClick={this.handleClickFacebookLogin}
                />
            </styled.SocialLoginButtonWrapper>
        ) : null;
    }

    maybeRenderDividerText = () =>
        this.state.isPatientMode ? (
            <styled.StandardLoginDividerText data-test="standard-login-divider-text">
                or
            </styled.StandardLoginDividerText>
        ) : null;

    renderSignUpButton() {
        return (
            <styled.SignupButton
                data-test="create-account-button"
                fluid
                onClick={this.handleClickCreateAccount}
            >
                Sign up with email
            </styled.SignupButton>
        );
    }

    renderToggle() {
        const patientOrProviderToggleText = this.state.isPatientMode
            ? 'Provider login'
            : 'Patient login';

        return this.props.allowToggle ? (
            <styled.ToggleWrapper>
                <styled.Toggle
                    data-test="patient-provider-mode-toggle"
                    onClick={this.toggleLoginMode}
                >
                    {patientOrProviderToggleText}
                </styled.Toggle>
            </styled.ToggleWrapper>
        ) : null;
    }

    render() {
        const {
            allowToggle,
            initialMode,
            onClickCreateAccount,
            onClickFacebookLogin,
            onClickGoogleLogin,
            onClickAppleLogin,
            onClickToggle,
            showFacebookLogin,
            showGoogleLogin,
            showAppleLogin,
            showRememberMe,
            socialLoginValidationError,
            ...props
        } = this.props;

        return (
            <div>
                {this.maybeRenderPatientModeButtons()}
                <styled.StandardLoginWrapper>
                    {this.maybeRenderDividerText()}
                    <styled.StandardLoginTitle data-test="standard-login-title">
                        Log in with email
                    </styled.StandardLoginTitle>

                    <styled.StyledSignInForm
                        data-test="styled-sign-in-form"
                        isPatientMode={this.state.isPatientMode}
                        showFacebookLogin={showFacebookLogin}
                        showRememberMe={showRememberMe}
                        socialLoginValidationError={socialLoginValidationError}
                        {...props}
                    />

                    {this.renderToggle()}
                </styled.StandardLoginWrapper>
            </div>
        );
    }

    getJsonStateParamForSocialLogin = () => {
        const { rememberMe, returnUrl, showRememberMe } = this.props;

        const { isPatientMode } = this.state;

        const stateQueryParam = {
            remember_me: rememberMe && isPatientMode && showRememberMe,
            return_url: returnUrl,
        };

        return encodeURIComponent(JSON.stringify(stateQueryParam));
    };

    handleClickSocialLogin = (loginType, event) => {
        event.preventDefault();

        const {
            facebookLoginUrl,
            googleLoginUrl,
            appleLoginUrl,
            onClickFacebookLogin,
            onClickGoogleLogin,
            onClickAppleLogin,
            returnUrl,
            showRememberMe,
        } = this.props;

        let redirectUrl;

        const stateQueryParam = showRememberMe
            ? this.getJsonStateParamForSocialLogin()
            : returnUrl;

        switch (loginType) {
            case LOGIN_TYPE.facebook:
                onClickFacebookLogin(event);
                redirectUrl = `${facebookLoginUrl}&state=${stateQueryParam}`;
                break;
            case LOGIN_TYPE.apple:
                onClickAppleLogin(event);
                redirectUrl = `${appleLoginUrl}&state=${stateQueryParam}`;
                break;
            case LOGIN_TYPE.google:
                onClickGoogleLogin(event);
                redirectUrl = `${googleLoginUrl}&state=${stateQueryParam}`;
                break;
            default:
                break;
        }

        redirectUser(redirectUrl);
    };

    handleClickFacebookLogin = event =>
        this.handleClickSocialLogin(LOGIN_TYPE.facebook, event);
    handleClickGoogleLogin = event =>
        this.handleClickSocialLogin(LOGIN_TYPE.google, event);
    handleClickAppleLogin = event =>
        this.handleClickSocialLogin(LOGIN_TYPE.apple, event);

    handleClickCreateAccount = event => {
        event.preventDefault();
        const { onClickCreateAccount, trackClickCreateAccount } = this.props;
        trackClickCreateAccount(event);
        onClickCreateAccount();
    };
}
